import Data from "../Data";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import Error from "../admin/components/Error";
import SplitColumns from "../account/components/SplitColumns";
import moment from "moment/moment";
import AppContext from "../../../context";
import {useParams} from "react-router-dom";
import SumupWidget from "../../components/SumupWidget";

const BundleConfirm = () => {
    const { id } = useParams()
    const context = useContext(AppContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false)
    const [sumupCheckoutId, setSumupCheckoutId] = useState(null);
    const [sumupPurchaseId, setSumupPurchaseId] = useState(null);
    const [sumupBookingId, setSumupBookingId] = useState(null);
    const [sumupError, setSumupError] = useState(null);
    const sumupPollRef = useRef(null);

    const getQueryParam = (param) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }

    const bundleId = getQueryParam('bundle');
    const ticketsParam = getQueryParam('tickets');
    const tickets = ticketsParam ? Number(ticketsParam) : 0;

    const [experience, setExperience] = useState([]);
    const [bundle, setBundle] = useState(null);
    const isSumup = context.domain?.paymentProcessor === 'SumUp';

    const getRedirectUrl = (payload) => {
        if(!payload) return null;
        if(typeof payload === "string") return payload;
        return payload.url || payload.checkout_url || payload.checkoutUrl || null;
    }

    useEffect(async () => {
        try {
            if(!bundleId) throw new Error('Missing bundle');
            const experience = await Data.fetchExperienceById({ id })
            setExperience(experience.data)
            const bundles = await Data.fetchBundlesForExperience({ experienceId: id })
            const selected = bundles.data.find(b => b._id === bundleId)
            if(!selected) throw new Error('Bundle not found')
            setBundle(selected)
        } catch (error) {
            window.location.href = "/"
        }
    }, []);

    const basePerPerson = useMemo(() => (experience.price || 0) * (bundle?.events?.length || 0), [experience, bundle])
    const savingPerPerson = useMemo(() => Math.max(0, basePerPerson - (bundle?.price || 0)), [basePerPerson, bundle])

    const book = async () => {
        setLoading(true)

        try {
            const res = await Data.createBundlePurchase({ bundleId, tickets });
            if(isSumup) {
                const checkoutId = res.data?.checkoutId || res.data?.id || null;
                const purchaseId = res.data?.purchaseId || null;
                const bookingId = res.data?.bookingId || null;
                if(!checkoutId || !purchaseId || !bookingId) throw new Error("Missing SumUp checkout details");
                setSumupCheckoutId(checkoutId);
                setSumupPurchaseId(purchaseId);
                setSumupBookingId(bookingId);
            } else {
                const redirectUrl = getRedirectUrl(res.data);
                if(!redirectUrl) throw new Error("Missing redirect URL");
                window.location.href = redirectUrl;
            }

            setError(false)
        } catch(error) {
            setError(true)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if(!sumupCheckoutId || !sumupPurchaseId || !sumupBookingId) return;
        if(sumupPollRef.current) {
            clearInterval(sumupPollRef.current);
        }

        let cancelled = false;
        const poll = async () => {
            try {
                const result = await Data.verifySumupBundlePurchase(sumupPurchaseId);
                if (!cancelled && result.status === 200) {
                    window.location.href = `/account/bookings/${sumupBookingId}?success=true&bundlePurchase=${sumupPurchaseId}`;
                }
            } catch (err) {
                // Keep polling while payment is pending; errors will surface on widget callbacks.
            }
        };

        poll();
        sumupPollRef.current = setInterval(poll, 4000);

        return () => {
            cancelled = true;
            if(sumupPollRef.current) {
                clearInterval(sumupPollRef.current);
                sumupPollRef.current = null;
            }
        };
    }, [sumupCheckoutId, sumupPurchaseId, sumupBookingId]);

    return (
        <div className={"p-8"}>
            <div className={"container mx-auto"}>
                <div className={"grid grid-cols-1 gap-4 "}>
                    <h1>Bundle Summary</h1>

                    <div className={"border rounded-lg p-6"}>
                        <SplitColumns>
                            <p>Your Name</p>
                            <p>{context.user.firstName} {context.user.lastName}</p>
                            <p>Your Email Address</p>
                            <p>{context.user.email}</p>
                            <p>Experience</p>
                            <p>{experience.name}</p>
                            <p>Events</p>
                            <p>{bundle?.events?.length}</p>
                            <p>Tickets</p>
                            <p>{tickets} ticket{tickets > 1 ? 's' : ''}</p>
                            <p>Standard price (per person)</p>
                            <p>£{basePerPerson}</p>
                            <p>Bundle price (per person)</p>
                            <p>£{bundle?.price}</p>
                            <p>You save</p>
                            <p>£{savingPerPerson} per person</p>
                            <p>Total Amount</p>
                            <p>£{(tickets * (bundle?.price || 0)).toFixed(2)}</p>
                        </SplitColumns>
                    </div>

                    <div>
                    <button disabled={loading || !!sumupCheckoutId}
                                className={"inline-block bg-primary py-2 px-4 text-white text-center rounded-md border-0"}
                                onClick={book}>{loading ? 'Redirecting...' : sumupCheckoutId ? 'Payment Ready' : 'Continue to Payment'}</button>
                    </div>

                    {sumupCheckoutId ? (
                        <div className={"mt-6"}>
                            <SumupWidget
                                checkoutId={sumupCheckoutId}
                                onSuccess={async () => {
                                    try {
                                        setSumupError(null);
                                        const result = await Data.verifySumupBundlePurchase(sumupPurchaseId);
                                        if (result.status === 200) {
                                            window.location.href = `/account/bookings/${sumupBookingId}?success=true&bundlePurchase=${sumupPurchaseId}`;
                                        }
                                    } catch (err) {
                                        setSumupError('There was an issue verifying your payment. Please refresh.');
                                    }
                                }}
                                onError={(message) => {
                                    setSumupError(message || 'Payment failed');
                                }}
                            />
                        </div>
                    ) : null}

                    {sumupError ? <Error message={sumupError} /> : null}

                    {error ? <Error message={"There was an error creating your bundle purchase, please try again."}/> : null}
                </div>
            </div>
        </div>
    )
}

export default BundleConfirm
